import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Grid,
  Snackbar,
  TextField,
  Autocomplete,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useLocation } from "react-router-dom";
import InventoryIcon from '@mui/icons-material/Inventory';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PersonIcon from "@mui/icons-material/Person";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Chip, Tooltip, IconButton } from "@mui/material";

import egressService from "../../../services/egressService";
import ingressService from "../../../services/ingressService";
import userService from "../../../services/userService";
import productsService from "../../../services/productsService";

import TablaGenerica from "../../../components/Table/tablaGenerica";
import StepMetodoSalida from "./components/StepMetodoSalida";
import StepPackages from "./components/StepPackages";
import EgressLimitModal from "../../../components/EgressLimitModal";


const initialAlert = { severity: "success", message: "" };

// Borrador persistido: permite retomar un egreso a medio cargar.
const DRAFT_KEY = "egress-draft";

// El método de salida define qué pasos hacen falta. "Cliente retira"
// (mostrador) no necesita dimensionar bultos, así que su flujo es más corto.
const STEP_LABELS = {
  productos: "Productos",
  bultos: "Bultos",
  destino: "Destino",
  confirmacion: "Confirmación",
};
// Envío convencional (red_despacho): los bultos + medidas se cargan en la CREACIÓN
// (paso "bultos", antes de "destino" porque la cotización los necesita). El resto de
// operativas no dimensiona bultos en la creación (los arma el operario al preparar).
const STEP_FLOWS = {
  cliente: ["productos", "destino", "confirmacion"],
  red_despacho: ["productos", "bultos", "destino", "confirmacion"],
  redespacho: ["productos", "destino", "confirmacion"],
  transporte_manual: ["productos", "destino", "confirmacion"],
};
const DEFAULT_FLOW = ["productos", "destino", "confirmacion"];

const metodoOptions = [
  { key: "cliente", icon: <PersonIcon />, title: "Cliente retira", description: "En mostrador" },
  { key: "red_despacho", icon: <LocalShippingIcon />, title: "Envío convencional", description: "OCA, Andreani, etc." },
  { key: "redespacho", icon: <SyncAltIcon />, title: "Redespacho", description: "Dos transportes" },
  { key: "transporte_manual", icon: <LocalShippingOutlinedIcon />, title: "Transporte retira", description: "Sin integración" },
];

export default function CreateEgress() {
  const navigate = useNavigate();
  const location = useLocation();
  // Modo "completar": cuando se llega con ?completar=<id>, se está asignando la
  // operativa a una venta de integración ya existente (no se crea un egreso nuevo).
  const completarId = new URLSearchParams(location.search).get("completar");
  const [completarEgreso, setCompletarEgreso] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [tipoRetiroSeleccionado, setTipoRetiroSeleccionado] = useState(null);

  const [filteredProducts, setFilteredProducts] = useState([]);
  const [textEntered, setTextEntered] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [alertObj, setAlertObj] = useState(initialAlert);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [amountEntered, setAmountEntered] = useState(0);
  const [cantidadIngresadaErrors, setCantidadIngresadaErrors] = useState({});
  const [inputFocus, setInputFocus] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  // Cartel de límite de plan (soft-cap): se llena al recibir 402 egress_limit_reached.
  const [limitModal, setLimitModal] = useState(null);

  const [datosDestino, setDatosDestino] = useState({
    cliente: {
      nombre: "",
      id: "",
      telefono: "",
      email: "",
      direccion: "",
      localidad: "",
      provincia: "",
      cp: "",
    },
    transportes: {
      t1_empresa: "",
      t1_patente: "",
      t1_telefono: "",
      t2_empresa: "",
      t2_patente: "",
      t2_telefono: "",
    },
  });
  const [packages, setPackages] = useState([]);
  /** Valor del input "Cantidad de bultos" al avanzar desde el paso Bultos (step 3). */
  const [packageCount, setPackageCount] = useState(null);
  const [multipackComponentsBySku, setMultipackComponentsBySku] = useState({});

  const [productsMap, setProductsMap] = useState({});

  // Confirmación al cancelar y borrador para retomar.
  const [confirmExit, setConfirmExit] = useState(false);
  const [draftPrompt, setDraftPrompt] = useState(false);

  // Pasos visibles según el método elegido (key-based, no por índice fijo).
  const stepKeys = STEP_FLOWS[tipoRetiroSeleccionado] || DEFAULT_FLOW;
  const steps = stepKeys.map((k) => STEP_LABELS[k]);
  const currentStep = stepKeys[activeStep];
  const hayDatos = selectedProducts.length > 0;

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const clearDraft = () => localStorage.removeItem(DRAFT_KEY);

  const handleCancel = () => {
    if (hayDatos) setConfirmExit(true);
    else navigate("/egress-list");
  };

  const discardAndExit = () => {
    clearDraft();
    navigate("/egress-list");
  };

  const restoreDraft = () => {
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY));
      if (d) {
        setSelectedProducts(d.selectedProducts || []);
        setTipoRetiroSeleccionado(d.tipoRetiroSeleccionado || null);
        if (d.datosDestino) setDatosDestino(d.datosDestino);
        setPackages(d.packages || []);
        setPackageCount(d.packageCount ?? null);
        setProductsMap(d.productsMap || {});
        setActiveStep(d.activeStep || 0);
      }
    } catch (e) {
      console.error("No se pudo restaurar el borrador", e);
    }
    setDraftPrompt(false);
  };

  const discardDraft = () => {
    clearDraft();
    setDraftPrompt(false);
  };

  // Al montar, ofrecer retomar si hay un borrador con productos.
  // En modo "completar" no aplica: los datos vienen sembrados desde la venta.
  useEffect(() => {
    if (completarId) return;
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (d?.selectedProducts?.length) setDraftPrompt(true);
    } catch (e) {
      localStorage.removeItem(DRAFT_KEY);
    }
  }, [completarId]);

  // Modo "completar": sembrar productos + datos del comprador desde la venta de
  // integración para que el operario solo asigne la operativa (+ bultos/transporte).
  useEffect(() => {
    if (!completarId) return;
    let cancelado = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await ingressService.getIngressById(completarId);
        const egr = resp?.data;
        if (cancelado || !egr) return;

        const prods = (egr.movement_details || []).map((d) => ({
          id: d.product_id ?? d.sku_id,
          product_id: d.product_id,
          sku_id: d.sku_id,
          name: d.description || d.name || "",
          amountEntered: Number(d.original_count || d.effective_count || 1),
        }));

        let logistics = egr.logistics;
        if (typeof logistics === "string") {
          try { logistics = JSON.parse(logistics); } catch (e) { logistics = null; }
        }
        const dd = logistics?.deliveryDetails || {};
        const addr = dd.address || {};
        const contact = dd.contact || {};

        // Las integraciones mandan la dirección en una sola línea ("Av. Colón 1234").
        // Si no viene el número separado, lo extraemos del final de la calle.
        let calle = addr.street || "";
        let altura = addr.streetNumber || "";
        if (!altura && calle) {
          const mNum = calle.match(/^(.*?)[\s,]+(\d+[a-zA-Z]?)\s*$/);
          if (mNum) { calle = mNum[1].trim(); altura = mNum[2]; }
        }

        setDatosDestino((prev) => ({
          ...prev,
          cliente: {
            ...prev.cliente,
            nombre: dd.receiverName || egr.provider_name || "",
            id: dd.receiverId || "",
            telefono: contact.phone || egr.phone_number || "",
            email: contact.email || egr.email || "",
            direccion: calle,
            altura: altura,
            piso_dpto: addr.floor || "",
            localidad: addr.city || "",
            provincia: addr.province || "",
            cp: addr.zipCode || "",
            referencia: addr.reference || "",
          },
        }));
        setSelectedProducts(prods);
        setCompletarEgreso(egr);
      } catch (e) {
        console.error("No se pudo cargar la venta a completar", e);
        setAlertObj({ severity: "error", title: "Error", message: "No se pudo cargar la venta." });
        setOpenSnackbar(true);
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();
    return () => { cancelado = true; };
  }, [completarId]);

  // Guardado automático del borrador (mientras el usuario no esté decidiendo
  // si retoma uno previo).
  useEffect(() => {
    if (completarId) return;
    if (draftPrompt) return;
    if (selectedProducts.length === 0) return;
    try {
      localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({
          activeStep,
          tipoRetiroSeleccionado,
          selectedProducts,
          datosDestino,
          packages,
          packageCount,
          productsMap,
        })
      );
    } catch (e) {
      /* cuota llena: ignorar */
    }
  }, [activeStep, tipoRetiroSeleccionado, selectedProducts, datosDestino, packages, packageCount, productsMap, draftPrompt, completarId]);

  // Avisar al cerrar/recargar la pestaña si hay datos sin guardar.
  useEffect(() => {
    const handler = (e) => {
      if (selectedProducts.length > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [selectedProducts]);

  // Calcula el consumo acumulado de componentes base por todos los productos
  // del egreso EXCEPTO el producto que se está editando (excludeSku).
  // Retorna un mapa { componentId: cantidadConsumida }
  const getComponentConsumption = (excludeSku) => {
    const consumption = {};
    for (const p of selectedProducts) {
      if (p.sku_id === excludeSku) continue;
      const qty = parseInt(p.amountEntered) || 0;
      if (qty <= 0) continue;

      if (p.is_combo && Array.isArray(p.combo)) {
        for (const c of p.combo) {
          consumption[c.product_id] = (consumption[c.product_id] || 0) + qty * c.quantity;
        }
      } else {
        consumption[p.id] = (consumption[p.id] || 0) + qty;
      }
    }
    return consumption;
  };

  // Calcula el stock disponible real para un producto, descontando lo que
  // los demás productos del egreso ya consumirían de sus componentes.
  const getAvailableStock = (product, excludeSku) => {
    const consumption = getComponentConsumption(excludeSku);

    if (product?.is_combo) {
      const combo = product.combo || [];
      if (!combo.length) return 0;

      return Math.min(
        ...combo.map((c) => {
          const productBase = productsMap[c.product_id];
          if (!productBase) return 0;
          const baseDisponible = (productBase.stock || 0) - (productBase.stock_out || 0);
          const usedByOthers = consumption[c.product_id] || 0;
          return Math.floor((baseDisponible - usedByOthers) / c.quantity);
        })
      );
    } else {
      const baseDisponible = (product?.stock || 0) - (product?.stock_out || 0);
      const usedByOthers = consumption[product?.id] || 0;
      return baseDisponible - usedByOthers;
    }
  };

  const columns = [
    {
      title: "Producto",
      field: "name",
      sorting: false,
      width: 200,
      render: (rowData) => (
        <Typography variant="body2" fontWeight={500}>{rowData.name}</Typography>
      ),
    },
    {
      title: "SKU",
      field: "sku_id",
      sorting: false,
      width: 50,
      render: (rowData) => (
        <Chip
          label={rowData.sku_id}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            backgroundColor: "#E8F0FE",
            color: "#3949ab",
          }}
        />
      ),
    },
    {
      title: "Stock Disp.",
      field: "stock_disponible",
      sorting: false,
      width: 50,
      render: (rowData) => {
        const available = getAvailableStock(rowData, rowData.sku_id);
        return (
          <Typography variant="body2" fontWeight={500}>
            {Math.max(0, available)}
          </Typography>
        );
      },
    },
    {
      title: "Egreso",
      field: "stock_in",
      sorting: false,
      width: 20,
      render: (rowData) => (
        <TextField
          variant="outlined"
          key={rowData?.sku_id}
          size="small"
          placeholder="0"
          sx={{
            width: 80,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              fontSize: "0.875rem",
              "& fieldset": { borderColor: "#cbd5e1" },
              "&:hover fieldset": { borderColor: "primary.main" },
              "&.Mui-focused fieldset": { borderWidth: 2 },
            },
            "& input": { textAlign: "center", py: 0.8 },
          }}
          value={rowData.amountEntered ?? ""}
          onChange={(event) => handleQuantityChange(event, rowData.sku_id)}
          error={!!cantidadIngresadaErrors[rowData.sku_id]}
          helperText={cantidadIngresadaErrors[rowData.sku_id]}
          autoFocus={inputFocus === rowData.sku_id}
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
          }}
        />
      ),
    },
    {
      title: "Acción",
      field: "accion",
      sorting: false,
      width: 80,
      render: (rowData) => (
        <Tooltip title="Eliminar producto" arrow>
          <IconButton
            size="small"
            onClick={() => handleRemoveProduct(rowData.sku_id)}
            sx={{
              color: "#94a3b8",
              "&:hover": { color: "#ef4444", backgroundColor: "#fef2f2" },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  useEffect(() => {
    fetchDepositos();
  }, []);

  useEffect(() => {
    if (textEntered.length < 3) {
      setFilteredProducts([]);
      setIsSearching(false);
      return undefined;
    }

    const debounceTimer = setTimeout(() => {
      filterProductsByText(textEntered);
    }, 700);

    return () => clearTimeout(debounceTimer);
  }, [textEntered]);

  useEffect(() => {
    const total = selectedProducts.reduce(
      (acc, producto) => acc + (parseInt(producto.amountEntered, 10) || 0),
      0
    );
    setAmountEntered(total);
  }, [selectedProducts]);

  useEffect(() => {
    selectedProducts
      .filter((p) => p.is_multipack && multipackComponentsBySku[p.sku_id] == null)
      .forEach((p) => fetchMultipackComponents(p.sku_id));
  }, [selectedProducts]);


  const fetchDepositos = async () => {
    const result = await userService.getDeposits();
    setDeposits(result.data);
  };

  const filterProductsByText = async (text) => {
    setIsSearching(true);
    try {
      const res = await ingressService.getInventory(text, true);
      const results = res?.data?.results || [];
      setFilteredProducts(results);

      const map = {};
      results.forEach(p => {
        map[p.id] = p;
      });
      setProductsMap(prev => ({ ...prev, ...map }));

    } catch (error) {
      console.error("Error al filtrar productos:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const fetchMultipackComponents = async (productId) => { // Cambiamos nombre a productId para claridad
    try {
      const res = await productsService.getProductsBySku(productId);
      const data = Array.isArray(res?.data) ? res.data : res?.data ? [res.data] : [];
      const children = data.filter((item) => item.is_multipack === false);

      setMultipackComponentsBySku((prev) => ({
        ...prev,
        [productId]: children, // Guardamos usando el ID como llave
      }));
    } catch (err) {
      setMultipackComponentsBySku((prev) => ({ ...prev, [productId]: [] }));
    }
  };

  const handleProductSelect = (event, value) => {
    if (value) {
      const existentProduct = selectedProducts.find((p) => p.id === value.id);
      if (!existentProduct) {
        setSelectedProducts((prev) => [{ ...value, amountEntered: "" }, ...prev]);

        if (value.is_multipack) {
          fetchMultipackComponents(value.id); // Usamos el ID
        }
        if (value.is_combo) {
          fetchComboById(value.id);
        }
      }
    }
  };

  const fetchComboById = async (productId) => {
    try {
      const res = await productsService.getProductsBySku(productId);
      const productFromApi = res.data?.[0];

      if (!productFromApi) return;

      setSelectedProducts((prev) =>
        prev.map((p) =>
          p.id === productId
            ? { ...p, combo: productFromApi.combo }
            : p
        )
      );

      productFromApi.combo.forEach(async (c) => {
        const resChild = await productsService.getProductsBySku(c.product_id);
        const child = resChild.data?.[0];

        if (!child) return;

        setProductsMap((prev) => ({
          ...prev,
          [child.id]: child,
        }));
      });

    } catch (e) {
      console.error("Error trayendo combo", e);
    }
  };

  const handleQuantityChange = (event, sku) => {
    const value = event.target.value;

    if (value !== "" && !/^\d+$/.test(value)) return;

    const errors = { ...cantidadIngresadaErrors };
    const product = selectedProducts.find((p) => p.sku_id === sku);
    const stockDisponible = Math.max(0, getAvailableStock(product, sku));

    let errorMsg = "";
    if (value.trim() !== "") {
      const numericValue = parseInt(value, 10);
      if (numericValue > stockDisponible) errorMsg = `Máximo disponible: ${stockDisponible}`;
      else if (numericValue <= 0) errorMsg = "Ingresá al menos 1";
    }

    errors[sku] = errorMsg;
    setCantidadIngresadaErrors(errors);
    setInputFocus(sku);
    setSelectedProducts((prev) =>
      prev.map((p) => (p.sku_id === sku ? { ...p, amountEntered: value } : p))
    );
  };

  const handleRemoveProduct = (sku) => {
    setSelectedProducts((prev) => prev.filter((p) => p.sku_id !== sku));
  };

  const canConfirm = () => {
    const hasErrors = Object.values(cantidadIngresadaErrors).some(
      (e) => e !== ""
    );
    return (
      selectedProducts.length > 0 &&
      selectedProducts.every(
        (p) => p.amountEntered && parseInt(p.amountEntered) > 0
      ) &&
      !hasErrors
    );
  };

  const handleConfirmEgress = async () => {
    const hasCantidadIngresadaErrors = Object.values(cantidadIngresadaErrors).some(
      (error) => error !== ""
    );

    if (hasCantidadIngresadaErrors || !canConfirm()) {
      setAlertObj({ severity: "error", message: "Faltan completar las cantidades de algunos productos." });
      setOpenSnackbar(true);
      return;
    }

    const logistics = buildLogistics();

    // Modo "completar": se asigna la operativa a una venta de integración ya
    // existente. Se actualiza el egreso (queda "pending"), no se crea uno nuevo.
    if (completarEgreso) {
      const updateBody = {
        id: completarEgreso.id,
        type: "out",
        status: "pending",
        // user_id es obligatorio en el endpoint Update (no puede ser 0).
        user_id: completarEgreso.user_id || parseInt(sessionStorage.getItem("User"), 10) || 0,
        sub_movement_id: completarEgreso.sub_movement_id || 0,
        receipt_id: completarEgreso.receipt_id || "",
        provider_name: datosDestino?.cliente?.nombre || completarEgreso.provider_name || "",
        warehouse_id: completarEgreso.warehouse_id,
        phone_number: datosDestino?.cliente?.telefono || "",
        email: datosDestino?.cliente?.email || "",
        observation: completarEgreso.observation || "",
        // Preservar la integración (canal + transaction_id) para no perder el origen.
        integration: completarEgreso.integration || undefined,
        package_count: packageCount != null ? packageCount : packages.length,
        movement_details: selectedProducts.map((product) => ({
          product_id: (product?.product_id ?? product?.id ?? product?.sku_id).toString(),
          description: product?.name ?? "",
          original_count: Number(product.amountEntered),
          effective_count: 0,
        })),
        packages: Array.isArray(packages)
          ? packages.map((pkg, idx) => ({
              index: idx + 1,
              width: Number(pkg.width),
              height: Number(pkg.height),
              depth: Number(pkg.depth),
              declared_value: Number(pkg.declared_value),
              total_weight: Number(pkg.total_weight),
            }))
          : [],
        logistics,
      };

      try {
        setLoading(true);
        await ingressService.postRegisterIngress(updateBody);
        clearDraft();
        navigate("/egress-list");
        setAlertObj({ severity: "success", message: "Operativa asignada." });
        setOpenSnackbar(true);
      } catch (error) {
        const msg = error?.response?.data?.error || error?.response?.data?.Error;
        setAlertObj({
          severity: "error",
          message: msg || "No pudimos asignar la operativa. Probá de nuevo.",
        });
        setOpenSnackbar(true);
      } finally {
        setLoading(false);
      }
      return;
    }

    const warehouse_id =
      deposits?.length === 1 ? deposits[0]?.id : deposits?.[0]?.id ?? 0;
    console.log(selectedProducts);

    const requestBody = {
      type: "out",
      status: "pending",
      sub_movement_id: 0,
      receipt_id: "",
      provider_name: datosDestino?.cliente?.nombre || "",
      warehouse_id,
      phone_number: datosDestino?.cliente?.telefono || "",
      email: datosDestino?.cliente?.email || "",
      observation: "",
      package_count: packageCount != null ? packageCount : packages.length,
      movement_details: selectedProducts.map((product) => ({
        product_id: (product?.id ?? product?.product_id ?? product?.sku_id).toString(),
        description: product?.name ?? product?.name ?? "",
        original_count: Number(product.amountEntered),
        effective_count: 0,
      })),
      packages: Array.isArray(packages)
        ? packages.map((pkg, idx) => ({
          index: idx + 1,
          width: Number(pkg.width),
          height: Number(pkg.height),
          depth: Number(pkg.depth),
          declared_value: Number(pkg.declared_value),
          total_weight: Number(pkg.total_weight),
        }))
        : [],
      logistics,
    };

    try {
      setLoading(true);
      const dataEgress = await egressService.PostCreateEgress(requestBody);

      if (dataEgress?.data && !dataEgress.data?.Error) {
        // Egreso creado — etiqueta se imprime al confirmar desde el listado
        clearDraft();
        navigate("/egress-list");

        setAlertObj({
          severity: "success",
          message: "Egreso creado.",
        });
        setOpenSnackbar(true);
      } else if (dataEgress?.data?.Error) {
        setAlertObj({
          severity: "error",
          message: "No pudimos crear el egreso. Revisá tu conexión y probá de nuevo.",
        });
        setOpenSnackbar(true);
      }
    } catch (error) {
      const errData = error?.response?.data;
      if (error?.response?.status === 402 && errData?.code === "egress_limit_reached") {
        // Soft-cap alcanzado: cartel de upgrade en vez del toast genérico.
        setLimitModal({ plan: errData.plan, limit: errData.limit, used: errData.used });
      } else {
        setAlertObj({
          severity: "error",
          message: "No pudimos crear el egreso. Revisá tu conexión y probá de nuevo.",
        });
        setOpenSnackbar(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const buildLogistics = () => {
    const shippingMethod = tipoRetiroSeleccionado;

    let deliveryDetails = {
      receiverName: null,
      receiverId: null,
      contact: {
        email: null,
        phone: null
      },
      address: {
        street: null,
        streetNumber: null,
        floor: null,
        city: null,
        zipCode: null,
        reference: null,
        country: "Argentina"
      },
      quote: {
        carrierName: null,
        serviceId: null,
        price: null,
        estimatedDays: null
      },
      thirdPartyCarrier: {
        intermediate: {
          companyName: null,
          licensePlate: null,
          phone: null
        },
        final: {
          companyName: null,
          licensePlate: null,
          phone: null
        }
      },
      pickupAtWarehouse: false,
      note: null
    };

    if (shippingMethod === 'cliente' || shippingMethod === 'retiro_puerta') {
      deliveryDetails.pickupAtWarehouse = true;
      deliveryDetails.receiverName = datosDestino.cliente.nombre || null;
      deliveryDetails.receiverId = datosDestino.cliente.id || null;
      deliveryDetails.contact.email = datosDestino.cliente.email || null;
      deliveryDetails.contact.phone = datosDestino.cliente.telefono || null;
      deliveryDetails.note = "Cliente retira en mostrador";
    }

    else if (shippingMethod === 'red_despacho') {
      deliveryDetails.pickupAtWarehouse = false;
      deliveryDetails.receiverName = datosDestino.cliente.nombre || null;
      deliveryDetails.receiverId = datosDestino.cliente.id || null;
      deliveryDetails.contact.email = datosDestino.cliente.email || null;
      deliveryDetails.contact.phone = datosDestino.cliente.telefono || null;

      deliveryDetails.address.street = datosDestino.cliente.direccion || null;
      deliveryDetails.address.streetNumber = datosDestino.cliente.altura || null;
      deliveryDetails.address.floor = datosDestino.cliente.piso_dpto || null;
      deliveryDetails.address.city = datosDestino.cliente.localidad || null;
      deliveryDetails.address.province = datosDestino.cliente.provincia || null;
      deliveryDetails.address.zipCode = datosDestino.cliente.cp || null;
      if (!deliveryDetails.address.zipCode && datosDestino.cliente.cp) {
        deliveryDetails.address.zipCode = datosDestino.cliente.cp;
      }
      deliveryDetails.address.reference = datosDestino.cliente.referencia || null;

      if (datosDestino.cotizacionSeleccionada) {
        deliveryDetails.quote.carrierName = datosDestino.cotizacionSeleccionada.provider_name || null;
        deliveryDetails.quote.serviceId = String(datosDestino.cotizacionSeleccionada.product_id) || null;
        deliveryDetails.quote.price = datosDestino.cotizacionSeleccionada.price || null;
        deliveryDetails.quote.estimatedDays = datosDestino.cotizacionSeleccionada.time_to_delibery || null;
      }

      deliveryDetails.note = `Envío convencional - ${deliveryDetails.quote.carrierName || 'Sin transportista seleccionado'}`;
    }

    // MÉTODO 3: Redespacho - Dos transportes (T1 y T2)
    else if (shippingMethod === 'redespacho') {
      deliveryDetails.pickupAtWarehouse = false;
      deliveryDetails.receiverName = datosDestino.cliente.nombre || null;
      deliveryDetails.receiverId = datosDestino.cliente.id || null;
      deliveryDetails.contact.email = datosDestino.cliente.email || null;
      deliveryDetails.contact.phone = datosDestino.cliente.telefono || null;

      // Dirección del destinatario final
      deliveryDetails.address.street = datosDestino.cliente.direccion || null;
      deliveryDetails.address.streetNumber = datosDestino.cliente.altura || null;
      deliveryDetails.address.floor = datosDestino.cliente.piso_dpto || null;
      deliveryDetails.address.city = datosDestino.cliente.localidad || null;
      deliveryDetails.address.province = datosDestino.cliente.provincia || null;
      deliveryDetails.address.zipCode = datosDestino.cliente.cp || null;
      deliveryDetails.address.reference = datosDestino.cliente.referencia || null;

      // Transporte intermedio (T1) - retira del depósito
      deliveryDetails.thirdPartyCarrier.intermediate.companyName = datosDestino.transportes.t1_empresa || null;
      deliveryDetails.thirdPartyCarrier.intermediate.licensePlate = datosDestino.transportes.t1_patente || null;
      deliveryDetails.thirdPartyCarrier.intermediate.phone = datosDestino.transportes.t1_telefono || null;

      // Transporte final (T2) - entrega al cliente final
      deliveryDetails.thirdPartyCarrier.final.companyName = datosDestino.transportes.t2_empresa || null;
      deliveryDetails.thirdPartyCarrier.final.licensePlate = datosDestino.transportes.t2_patente || null;
      deliveryDetails.thirdPartyCarrier.final.phone = datosDestino.transportes.t2_telefono || null;

      deliveryDetails.note = "Redespacho - Dos transportes";
    }

    // MÉTODO 4: Transporte retira (sin integración) - un solo transporte manual
    else if (shippingMethod === 'transporte_manual') {
      deliveryDetails.pickupAtWarehouse = false;
      deliveryDetails.receiverName = datosDestino.cliente.nombre || null;
      deliveryDetails.receiverId = datosDestino.cliente.id || null;
      deliveryDetails.contact.email = datosDestino.cliente.email || null;
      deliveryDetails.contact.phone = datosDestino.cliente.telefono || null;

      deliveryDetails.address.street = datosDestino.cliente.direccion || null;
      deliveryDetails.address.streetNumber = datosDestino.cliente.altura || null;
      deliveryDetails.address.floor = datosDestino.cliente.piso_dpto || null;
      deliveryDetails.address.city = datosDestino.cliente.localidad || null;
      deliveryDetails.address.province = datosDestino.cliente.provincia || null;
      deliveryDetails.address.zipCode = datosDestino.cliente.cp || null;
      deliveryDetails.address.reference = datosDestino.cliente.referencia || null;

      // Único dato del transporte: el nombre (reutiliza t1_empresa).
      deliveryDetails.thirdPartyCarrier.intermediate.companyName =
        datosDestino.transportes.t1_empresa || null;

      deliveryDetails.note = `Transporte retira - ${datosDestino.transportes.t1_empresa || "Sin nombre"}`;
    }

    return {
      shippingMethod: shippingMethod || null,
      deliveryDetails,
    };
  };

  return (
    <Grid container padding={5} spacing={3}>
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "#1a1a1a",
              pl: 1.5,
              borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
              lineHeight: 1.5,
            }}
          >
            Nuevo Egreso
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            p: 2,
            backgroundColor: "#fafafa",
          }}
        >
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
          }}
        >
          {currentStep === "productos" && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="#334155" mb={1.5}>
                ¿Cómo sale la mercadería?
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                {metodoOptions.map((opt) => {
                  const selected = tipoRetiroSeleccionado === opt.key;
                  return (
                    <Paper
                      key={opt.key}
                      elevation={0}
                      onClick={() => setTipoRetiroSeleccionado(opt.key)}
                      sx={{
                        flex: "1 1 200px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 2,
                        cursor: "pointer",
                        borderRadius: "12px",
                        border: selected ? "2px solid" : "1px solid #e2e8f0",
                        borderColor: selected ? "primary.main" : "#e2e8f0",
                        backgroundColor: selected ? "#eff4ff" : "#fff",
                        transition: "all 0.15s ease",
                        "&:hover": { borderColor: selected ? "primary.main" : "#94a3b8" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 40,
                          height: 40,
                          borderRadius: "10px",
                          bgcolor: selected ? "primary.main" : "#f1f5f9",
                          color: selected ? "#fff" : "#64748b",
                          flexShrink: 0,
                        }}
                      >
                        {React.cloneElement(opt.icon, { sx: { fontSize: 22 } })}
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {opt.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {opt.description}
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>

              <Typography variant="subtitle1" fontWeight={600} color="#334155" mb={2}>
                Productos a egresar
              </Typography>
              <Autocomplete
                key={selectedProducts.length}
                options={(() => {
                  const bySku = new Map();
                  filteredProducts.forEach((p) => bySku.set(p.sku_id, p));
                  return Array.from(bySku.values());
                })()}
                getOptionLabel={(option) =>
                  option ? `SKU: ${option.sku_id || ""} - ${option.name || ""}` : ""
                }
                isOptionEqualToValue={(option, value) => option.sku_id === value?.sku_id}
                onInputChange={(e, value, reason) => {
                  if (reason === "input") setTextEntered(value);
                }}
                onChange={(event, value) => {
                  handleProductSelect(event, value);
                  setTextEntered("");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscá por SKU o nombre (mín. 3 letras)"
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <SearchIcon sx={{ color: "#94a3b8", mr: 0.5 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={isSearching ? "Buscando..." : "No hay resultados"}
              />
              <Box mt={3}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    overflow: "hidden",
                  }}
                >
                  <TablaGenerica
                    data={selectedProducts}
                    columns={columns}
                    emptyMessage="Todavía no agregaste productos. Buscalos arriba para sumarlos al egreso."
                  />
                </Paper>
              </Box>
              {selectedProducts.length > 0 && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    backgroundColor: "#eff6ff",
                    border: "1px solid #dbeafe",
                    borderRadius: "12px",
                  }}
                >
                  <InfoOutlinedIcon sx={{ color: "#2563eb" }} />
                  <Typography variant="body2" sx={{ color: "#1e40af" }}>
                    Vas a egresar{" "}
                    <b>{amountEntered} unidades</b> de{" "}
                    <b>{selectedProducts.length} productos</b>.
                  </Typography>
                </Box>
              )}
              <Box mt={4} display="flex" justifyContent="space-between">
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleCancel}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    borderColor: "#cbd5e1",
                    color: "#475569",
                    "&:hover": {
                      borderColor: "#94a3b8",
                      backgroundColor: "#f8fafc",
                    },
                  }}
                >
                  Cancelar
                </Button>
                <Tooltip
                  title={
                    !tipoRetiroSeleccionado
                      ? "Elegí cómo sale la mercadería"
                      : !canConfirm()
                        ? "Agregá productos y sus cantidades"
                        : ""
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      disabled={!tipoRetiroSeleccionado || !canConfirm()}
                      onClick={handleNext}
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                      }}
                    >
                      Siguiente
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          )}

          {currentStep === "bultos" && (
            <StepPackages
              type={tipoRetiroSeleccionado}
              packages={packages}
              setPackages={setPackages}
              onPackageCountCommit={setPackageCount}
              onNext={handleNext}
              onBack={handleBack}
              selectedProducts={selectedProducts}
              multipackComponentsBySku={multipackComponentsBySku}
            />
          )}

          {currentStep === "destino" && (
            <StepMetodoSalida
              tipo={tipoRetiroSeleccionado}
              datos={datosDestino}
              setDatos={setDatosDestino}
              onNext={handleNext}
              onBack={handleBack}
              product_ids={selectedProducts
                .map((p) => p?.id ?? p?.product_id ?? p?.sku_id)
                .filter(Boolean)}
              packages={packages}
            />
          )}

          {currentStep === "confirmacion" && (
            <Box>
              <Box textAlign="center" py={2} mb={4}>
                <Typography variant="h5" fontWeight="bold" color="text.primary">
                  Revisá y confirmá el egreso
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Revisá que esté todo bien antes de generar el egreso.
                </Typography>
              </Box>

              <Grid container spacing={4} mb={2}>
                <Grid item xs={12} md={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      height: "100%",
                      borderColor: "#e2e8f0",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      display="flex"
                      alignItems="center"
                      gap={1}
                      mb={3}
                    >
                      <Box
                        component="span"
                        sx={{ color: "#3b82f6", display: "flex" }}
                      >
                        <InventoryIcon variant="outlined" />
                      </Box>
                      Productos a Egresar
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      {selectedProducts.map((item) => (
                        <Box
                          key={item.sku_id}
                          display="flex"
                          justifyContent="space-between"
                        >
                          <Typography variant="body2" color="text.secondary">
                            {item.name}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {item.amountEntered} u.
                          </Typography>
                        </Box>
                      ))}

                      <Divider sx={{ my: 1, borderStyle: "dashed" }} />

                      <Box
                        display="flex"
                        justifyContent="space-between"
                        color="primary.main"
                        fontWeight="bold"
                      >
                        <Typography fontWeight="bold">
                          Total Unidades
                        </Typography>
                        <Typography fontWeight="bold">
                          {selectedProducts.reduce(
                            (acc, i) => acc + (parseInt(i.amountEntered) || 0),
                            0
                          )}{" "}
                          u.
                        </Typography>
                      </Box>

                      {/* Los bultos se cargan en la packing station al preparar, no acá. */}

                      {tipoRetiroSeleccionado !== "cliente" &&
                        tipoRetiroSeleccionado !== "redespacho" &&
                        tipoRetiroSeleccionado !== "transporte_manual" && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        color="text.secondary"
                        fontWeight="bold"
                      >
                        <Typography fontWeight="bold">
                          Cantidad total declarada
                        </Typography>
                        <Typography fontWeight="bold">
                          $
                          {Array.isArray(packages)
                            ? packages.reduce((acc, pkg) => {
                              const declaredValue = parseFloat(pkg.declared_value || 0);
                              return acc + (isNaN(declaredValue) ? 0 : declaredValue);
                            }, 0)
                            : 0}
                        </Typography>
                      </Box>
                      )}

                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      height: "100%",
                      bgcolor: "#f8fafc",
                      borderColor: "#e2e8f0",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      display="flex"
                      alignItems="center"
                      gap={1}
                      mb={3}
                    >
                      <Box
                        component="span"
                        sx={{ color: "#3b82f6", display: "flex" }}
                      >
                        <LocalShippingIcon variant="outlined" />
                      </Box>
                      Logística & Destino
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2.5,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#94a3b8",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                            display: "block",
                            textAlign: "left"
                          }}
                        >
                          Método
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="600"
                          sx={{ textAlign: "left" }}
                        >
                          {{
                            cliente: "Cliente retira",
                            red_despacho: "Envío convencional",
                            redespacho: "Redespacho",
                            transporte_manual: "Transporte retira",
                          }[tipoRetiroSeleccionado] ||
                            tipoRetiroSeleccionado?.replace("_", " ") ||
                            "No definido"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#94a3b8",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                            display: "block",
                            textAlign: "left"
                          }}
                        >
                          Destinatario
                        </Typography>
                        <Typography variant="body2" fontWeight="600" sx={{ textAlign: "left" }}>
                          {datosDestino.cliente.nombre || "Sin especificar"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "left" }}>
                          {datosDestino.cliente.direccion}{" "}
                          {datosDestino.cliente.localidad}
                        </Typography>
                      </Box>

                      {tipoRetiroSeleccionado === "redespacho" && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#94a3b8",
                              fontWeight: "bold",
                              textTransform: "uppercase",
                              display: "block",
                              textAlign: "left"
                            }}
                          >
                            Transportista Intermedio
                          </Typography>
                          <Typography variant="body2" fontWeight="600" sx={{ textAlign: "left" }}>
                            {datosDestino.transportes.t1_empresa}
                          </Typography>
                        </Box>
                      )}

                      {tipoRetiroSeleccionado === "transporte_manual" && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#94a3b8",
                              fontWeight: "bold",
                              textTransform: "uppercase",
                              display: "block",
                              textAlign: "left"
                            }}
                          >
                            Transporte
                          </Typography>
                          <Typography variant="body2" fontWeight="600" sx={{ textAlign: "left" }}>
                            {datosDestino.transportes.t1_empresa || "Sin especificar"}
                          </Typography>
                        </Box>
                      )}

                      {tipoRetiroSeleccionado === "red_despacho" &&
                        datosDestino.cotizacionSeleccionada && (
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: "white",
                              border: "1px solid #e2e8f0",
                              borderRadius: "8px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="body2" fontWeight="bold" sx={{ textAlign: "left" }}>
                              {
                                datosDestino.cotizacionSeleccionada
                                  .provider_name.toUpperCase()
                              }
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color="primary"
                              sx={{ textAlign: "left" }}
                            >
                              ${" "}
                              {datosDestino.cotizacionSeleccionada.price.toLocaleString()}
                            </Typography>
                          </Box>
                        )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ mt: 4, mb: 3 }} />

              <Box display="flex" justifyContent="space-between">
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    px: 3,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: "#cbd5e1",
                    color: "#475569",
                    "&:hover": {
                      borderColor: "#94a3b8",
                      backgroundColor: "#f8fafc",
                    },
                  }}
                >
                  Volver
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmEgress}
                  disabled={loading}
                  startIcon={!loading && <CheckCircleOutlineIcon />}
                  sx={{
                    px: 4,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {loading ? "Generando egreso…" : "Generar egreso"}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Grid>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={alertObj.severity} sx={{ width: "100%" }}>
          {alertObj.message}
        </Alert>
      </Snackbar>

      {/* Cartel de upgrade cuando se alcanzó el cupo de egresos del plan (HTTP 402) */}
      <EgressLimitModal
        open={Boolean(limitModal)}
        onClose={() => setLimitModal(null)}
        plan={limitModal?.plan}
        limit={limitModal?.limit}
        used={limitModal?.used}
      />

      {/* Confirmación antes de descartar el egreso en curso */}
      <Dialog open={confirmExit} onClose={() => setConfirmExit(false)}>
        <DialogTitle>¿Descartar el egreso?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vas a perder los productos y datos que cargaste. Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmExit(false)} sx={{ textTransform: "none" }}>
            Seguir editando
          </Button>
          <Button color="error" onClick={discardAndExit} sx={{ textTransform: "none" }}>
            Descartar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retomar un egreso a medio cargar */}
      <Dialog open={draftPrompt}>
        <DialogTitle>Tenés un egreso sin terminar</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Querés retomarlo donde lo dejaste o empezar uno nuevo?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={discardDraft} sx={{ textTransform: "none" }}>
            Descartar borrador
          </Button>
          <Button variant="contained" onClick={restoreDraft} sx={{ textTransform: "none" }}>
            Retomar
          </Button>
        </DialogActions>
      </Dialog>

    </Grid>
  );
}
